import { useMemo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Skeleton } from '@/shared/ui/primitives';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/ui/shadcn/tabs';

import type { PatientNavigatorItemData, DateSortOrder, ScopedPatientTab } from '../model';
import { getPatientNavigatorComparator, useScopedPatientTabs } from '../model';
import { PatientNavigatorItem } from './PatientNavigatorItem';

interface PatientNavigatorProps {
  /** List of patients to display */
  patients: PatientNavigatorItemData[];
  /** Currently selected patient scope tab */
  activeTab: ScopedPatientTab;
  /** User ID of currently active patient (for highlight) */
  activeUserId?: string;
  /** Callback when a patient is selected */
  onSelectPatient: (userId: string) => void;
  dateSortOrder?: DateSortOrder;
  /** Optional callback when active tab changes */
  onActiveTabChange?: (tab: ScopedPatientTab) => void;
  /** Optional header content */
  header?: ReactNode;
  /** Optional footer content */
  footer?: ReactNode;
  /** Loading state for active patient dataset */
  isPatientsLoading?: boolean;
}

function PatientNavigatorListSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-1 overflow-y-auto px-1">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="rounded-lg px-2.5 py-2">
          <div className="flex items-center justify-between gap-2">
            <Skeleton className="h-3 w-28" />
            <div className="flex items-center gap-1">
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </div>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <Skeleton className="h-4 w-16 rounded-full" />
            <Skeleton className="h-3 w-36" />
            <Skeleton className="ml-auto h-3 w-12" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * PatientNavigator — inbox-style tabbed list of patient profiles.
 *
 * Uses shared patient tab logic (Urgent / Routine / conditional All)
 * so filtering is consistent with the dashboard queue.
 */
export function PatientNavigator({
  patients,
  activeTab,
  activeUserId,
  onSelectPatient,
  dateSortOrder = 'asc',
  onActiveTabChange,
  header,
  footer,
  isPatientsLoading = false,
}: PatientNavigatorProps) {
  const comparator = getPatientNavigatorComparator(dateSortOrder);
  const handleTabChange = onActiveTabChange ?? (() => {});
  const {
    availableTabs,
    effectiveTab,
    filtered: unsorted,
  } = useScopedPatientTabs({
    patients,
    activeTab,
    isPatientsLoading: isPatientsLoading ?? false,
    onTabChange: handleTabChange,
  });

  const filtered = useMemo(() => [...unsorted].sort(comparator), [comparator, unsorted]);

  return (
    <div className="flex h-full flex-col text-sidebar-foreground">
      {header !== undefined && <div className="shrink-0 pb-2">{header}</div>}

      {isPatientsLoading && patients.length === 0 ? (
        <PatientNavigatorListSkeleton />
      ) : (
        <Tabs
          value={effectiveTab ?? undefined}
          onValueChange={(next) => {
            const nextTab = next as ScopedPatientTab;
            onActiveTabChange?.(nextTab);
          }}
          className="flex flex-1 flex-col gap-0 overflow-hidden"
        >
          <div className="shrink-0 px-1 pb-2">
            <TabsList aria-label="Patient navigator tabs" className="h-7 gap-1">
              {availableTabs.map((tab) => {
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="h-6 gap-1 rounded-full px-2 text-xs data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
                  >
                    {tab.label}
                    {tab.id !== 'all' ? (
                      <Badge className="h-4 min-w-4 rounded-full border-none bg-white px-1 text-xs leading-none text-gray-700">
                        {tab.count}
                      </Badge>
                    ) : null}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto">
            {availableTabs.map((tab) => (
              <TabsContent key={tab.id} value={tab.id} className="flex flex-col gap-1">
                {effectiveTab === 'all' && tab.id === 'all' ? (
                  <div className="mx-1 mb-2 rounded-md border border-amber-300/50 bg-amber-400/10 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-200">
                    <div className="flex items-start gap-2">
                      <AlertTriangle
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500 dark:text-amber-300"
                        aria-hidden="true"
                      />
                      <p>
                        Showing all patients, including patients outside your licensed state or
                        practice areas.
                      </p>
                    </div>
                  </div>
                ) : null}
                {filtered.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {tab.id === 'all'
                      ? 'No patients found'
                      : `No ${tab.label.toLowerCase()} patients right now`}
                  </div>
                ) : (
                  filtered.map((patient) => (
                    <PatientNavigatorItem
                      key={patient.userId}
                      patient={patient}
                      isActive={patient.userId === activeUserId}
                      onSelect={() => onSelectPatient(patient.userId)}
                    />
                  ))
                )}
              </TabsContent>
            ))}
          </div>
        </Tabs>
      )}

      {footer !== undefined && <div className="shrink-0 pt-3">{footer}</div>}
    </div>
  );
}
